import { ILoan } from '../interfaces/i-api-loan'
import appConfig from '../app.config'
import {
	LoanType,
	PaymentReliefTypes,
	ProductType,
	SortPropType,
} from '../constants/enums/LoanType'
import { TFunction } from 'i18next'
import { IDeposit } from '../interfaces/i-api-deposit'
import {
	getCurrency,
	getLanguageLocale,
	stringifyNum,
	stringifyBooleanAsUi,
} from '../utils/helpers'
import {
	getCountryHeaders,
	LoanHeaders,
	LoanInfoHeaders,
	PaymentInfoHeaders,
} from '../constants/enums/CountryHeaders'
import React from 'react'
import { DocumentTypes } from 'exports'
import i18n from 'i18n'

const headers = getCountryHeaders(appConfig.country)

export function isLtMixedPricingDPD(loan: ILoan) {
	return (
		appConfig.country === 'lt' &&
		loan?.paymentReliefs?.some(
			(x) => x.type === PaymentReliefTypes.MixedPricingDPD
		)
	)
}

export function showBaseAndMarginRates(loan: ILoan) {
	const baseAndMarginProducts = [
		ProductType.FinCashLoan,
		ProductType.FinHalensLoan,
		ProductType.FinRefinLoan,
		ProductType.FinHalensLoanPpi,
		ProductType.FinCashLoanPpi,
		ProductType.FinRefinLoanPpi,
	]

	const isNewProduct = baseAndMarginProducts.includes(loan.product)

	return appConfig.country === 'fi' && isNewProduct
}

export function getLoanHeader(loanType: LoanType, t: TFunction, loan?: ILoan) {
	let data = [
		{
			text: LoanHeaders.LoanNumber,
			show: headers['LoanHeaders'].includes(LoanHeaders.LoanNumber),
			value: loan?.agreementNumber
				? loan?.agreementNumber
				: t(LoanType[loanType]),
			sortStr: SortPropType.agreementNumber,
			isMobile: true,
		},
		{
			text: LoanHeaders.Balance,
			show: headers['LoanHeaders'].includes(LoanHeaders.Balance),
			value: isLtMixedPricingDPD(loan!)
				? ''
				: stringifyNum(loan?.unpaidInvoices, {
						currency: getCurrency(),
						style: 'currency',
				  }),
			sortStr: SortPropType.unpaidInvoices,
			isMobile: true,
		},
		{
			text: LoanHeaders.TotalBalance,
			show: headers['LoanHeaders'].includes(LoanHeaders.TotalBalance),
			value: isLtMixedPricingDPD(loan!)
				? ''
				: stringifyNum(loan?.totalBalance, {
						currency: getCurrency(),
						style: 'currency',
				  }),
			sortStr: SortPropType.unpaidInvoices,
			isMobile: true,
		},
		{
			text:
				loanType === LoanType.Cash
					? LoanHeaders.Product
					: LoanHeaders.Partner,
			show:
				loanType === LoanType.Cash
					? headers['LoanHeaders'].includes(LoanHeaders.Product)
					: headers['LoanHeaders'].includes(LoanHeaders.Partner),
			value:

				loanType === LoanType.Cash
					? ProductType[loan?.product!]
					: loan?.externalPartnerName,
			sortStr: SortPropType.externalPartnerName,
			isMobile: false,
		},
		{
			text: LoanHeaders.OpeningDate,
			show: headers['LoanHeaders'].includes(LoanHeaders.OpeningDate),
			value: loan?.latestDisbursementDate
				? new Date(loan?.latestDisbursementDate)
						.toLocaleDateString(getLanguageLocale())
						.replaceAll('-', '.')
				: '',
			sortStr: '',
			isMobile: false,
		},
	]
	return data
}

export function getLoanPaymentInfo(loan: ILoan, t: TFunction) {
	let data = [
		{
			text: PaymentInfoHeaders.PastDueAmount,
			show:
				headers['PaymentInfoHeaders'].includes(
					PaymentInfoHeaders.PastDueAmount
				),
			value: stringifyNum(loan.unpaidInvoices, {
				currency: getCurrency(),
				style: 'currency',
			}),
		},
		{
			text: PaymentInfoHeaders.Amount,
			show:
				headers['PaymentInfoHeaders'].includes(
					PaymentInfoHeaders.Amount
				) && !isLtMixedPricingDPD(loan),
			value: stringifyNum(appConfig.country !== 'no' ? loan.unpaidInvoices : loan.lastIssuedInvoiceAmount, {
				currency: getCurrency(),
				style: 'currency',
			}),
		},
		{
			text: PaymentInfoHeaders.ExpirationDate,
			show:
				headers['PaymentInfoHeaders'].includes(
					PaymentInfoHeaders.ExpirationDate
				) && loan.latestDueDate !== null,
			value: new Date(loan.latestDueDate).toLocaleDateString(
				getLanguageLocale()
			),
		},
		{
			text: PaymentInfoHeaders.OCR,
			show: headers['PaymentInfoHeaders'].includes(
				PaymentInfoHeaders.OCR
			),
			value: loan.ocr,
			tooltip: ['ee', 'no'].includes(appConfig.country)
				? {
						value: t(appConfig.country === 'no' ? 'OCRLoansTooltipNO' : 'OCRLoansTooltip'),
				  }
				: undefined,
		},
		{
			text: PaymentInfoHeaders.PlusGiro,
			show: headers['PaymentInfoHeaders'].includes(
				PaymentInfoHeaders.PlusGiro
			),
			value: loan.iban,
		},
		{
			text: PaymentInfoHeaders.Receiver,
			show: headers['PaymentInfoHeaders'].includes(
				PaymentInfoHeaders.Receiver
			),
			value: t('ReceiverValue') as any,
		},
	]
	return data
}

export function getLoanInfo(loan: ILoan, loanType: LoanType, t: TFunction) {
	let data = [
		{
			text: LoanInfoHeaders.SignedCreditLimit,
			show:
				headers['LoanInfoHeaders'].includes(
					LoanInfoHeaders.SignedCreditLimit
				) &&
				loan.signedCreditLimit &&
				loanType === LoanType.CreditLine,
			value: stringifyNum(loan.signedCreditLimit, {
				currency: getCurrency(),
				style: 'currency',
			}),
			tooltip: i18n.exists('SignedCreditLimitTooltip')
				? {
						value: t('SignedCreditLimitTooltip'),
				  }
				: undefined,
		},
		{
			text: LoanInfoHeaders.CurrentPrincipal,
			show:
				headers['LoanInfoHeaders'].includes(
					LoanInfoHeaders.CurrentPrincipal
				) && !isLtMixedPricingDPD(loan),
			value: stringifyNum(loan.currentPrincipal, {
				currency: getCurrency(),
				style: 'currency',
			}),
			tooltip: {
				value: t('CurrentPrincipalTooltip'),
			},
		},
		{
			text: LoanInfoHeaders.TotalBalance,
			show: headers['LoanInfoHeaders'].includes(
				LoanInfoHeaders.TotalBalance
			),
			value: stringifyNum(loan.totalBalance, {
				currency: getCurrency(),
				style: 'currency',
			}),
			tooltip: {
				value:
					appConfig.country !== 'se'
						? t('TotalBalanceTooltip')
						: 'TotalBalanceTooltip',
			},
		},
		{
			text: LoanInfoHeaders.MonthlyAmount,
			show:
				headers['LoanInfoHeaders'].includes(
					LoanInfoHeaders.MonthlyAmount
				) && !isLtMixedPricingDPD(loan),
			value: stringifyNum(
				// ['ee', 'lv', 'lt'].includes(appConfig.country)
				// 	? loan.monthlyCost
				// 	: loan.lastIssuedInvoiceAmount,
				loan.lastIssuedInvoiceAmount,
				{
					currency: getCurrency(),
					style: 'currency',
				}
			),
		},
		{
			text: LoanInfoHeaders.Overpayment,
			show:
				headers['LoanInfoHeaders'].includes(
					LoanInfoHeaders.Overpayment
				) && !isLtMixedPricingDPD(loan),
			value: stringifyNum(loan.overPayment, {
				currency: getCurrency(),
				style: 'currency',
			}),
		},
		{
			text: LoanInfoHeaders.BaseInterestRate,
			show:
				headers['LoanInfoHeaders'].includes(
					LoanInfoHeaders.BaseInterestRate
				) &&
				!isLtMixedPricingDPD(loan) &&
				showBaseAndMarginRates(loan),
			value: stringifyNum(loan.baseInterestRate, {
				style: 'percent',
			}),
		},
		{
			text: LoanInfoHeaders.Interest,
			show:
				headers['LoanInfoHeaders'].includes(LoanInfoHeaders.Interest) &&
				!isLtMixedPricingDPD(loan),
			value: stringifyNum(loan.interestRate, {
				style: 'percent',
			}),
		},
		{
			text: LoanInfoHeaders.OriginalAmount,
			show: headers['LoanInfoHeaders'].includes(
				LoanInfoHeaders.OriginalAmount
			),
			value: stringifyNum(loan.originalAmount, {
				currency: getCurrency(),
				style: 'currency',
			}),
		},
		{
			text: LoanInfoHeaders.Ppi,
			show: headers['LoanInfoHeaders'].includes(LoanInfoHeaders.Ppi),
			value: t(stringifyBooleanAsUi(loan.ppi)),
		},
		{
			text: LoanInfoHeaders.PpiValidFrom,
			show:
				loan.ppi &&
				headers['LoanInfoHeaders'].includes(LoanInfoHeaders.Ppi),
			value: new Date(loan.ppiValidFrom).toLocaleDateString(
				getLanguageLocale()
			),
		},
		{
			text: LoanInfoHeaders.IsPaymentHolidayPossible,
			show: headers['LoanInfoHeaders'].includes(
				LoanInfoHeaders.IsPaymentHolidayPossible
			),
			value: t(stringifyBooleanAsUi(loan.isPaymentHolidayPossible)),
		},
	]
	return data
}

export function downloadRepaymentplan(loan: ILoan) {
	return (
		appConfig.country !== 'se' &&
		appConfig.country !== 'no' &&
		!isLtMixedPricingDPD(loan) &&
		loan.repaymentPlanID !== '00000000-0000-0000-0000-000000000000'
	)
}

export function downloadSignedPromissoryNote(loan: ILoan) {
	return (
		['ee', 'lv'].includes(appConfig.country) &&
		!isLtMixedPricingDPD(loan) &&
		loan.documentsInfo?.some(
			(x) => x.type === DocumentTypes.SignedPromissoryNote
		)
	)
}

export function downloadPromissoryNote(loan: ILoan) {
	return (
		['lt'].includes(appConfig.country) &&
		loan.documentsInfo?.some((x) => x.type === DocumentTypes.PromissoryNote)
	)
}

export function getDataDeposit(deposit: IDeposit, t: TFunction) {
	let data = [
		{
			text: 'Balance2',
			value: (
				<b>
					{stringifyNum(deposit.balance, {
						currency: getCurrency(),
						style: 'currency',
					})}
				</b>
			),
		},
		{
			text: 'Term',
			value: (
				<b>
					{new Date(deposit.term).toLocaleDateString(
						getLanguageLocale()
					)}
				</b>
			),
		},
		{
			text: 'PresetDisbursementAccount',
			value: (
				<b>
					{deposit.presetWithdrawalAccountPrefix +
						' ' +
						deposit.presetWithdrawalAccount}
				</b>
			),
			tooltip: {
				value: t('PresetWithdrawalAccountTooltip'),
			},
		},
		{
			text: 'TransactionFee',
			value: deposit.type.toLowerCase().startsWith('f1') ? (
				<b>{t('FixedRateTransactionFee')}</b>
			) : (
				<b>{t('DepositPlusTransactionFee')}</b>
			),
			tooltip: {
				value: deposit.type.toLowerCase().startsWith('f1')
					? t('FixedRateTooltip')
					: t('DepositPlusTooltip'),
			},
		},
		{
			text: 'InterestrateDeposit',
			value: (
				<b>
					{stringifyNum(deposit.interestRate, {
						style: 'percent',
					})}
				</b>
			),
		},
	]

	if(appConfig.country === "fi")
	{
		data.push(
			{
				text: 'AccruedInterestDeposit',
				value: (
					<b>
						{stringifyNum(deposit.accruedInterest, {
							currency: getCurrency(),
							style: 'currency',
					})}
					</b>
				),
			})
	}

	return data
}

export function getDepositInfo(deposit: IDeposit, t: TFunction) {
	var array
	if (appConfig.country === 'se') {
		array = [
			{
				text: 'Bankgiro',
				value: deposit.bankGiroDeposit,
				tooltip: undefined,
			},
			{
				text: 'PlusGiro',
				value: deposit.plusGiroDeposit,
				tooltip: undefined,
			},
			{
				text: 'OCRDeposits',
				value: deposit.ocr,
				tooltip: undefined,
			},
		]
	} else {
		array = [
			{
				text: 'Bic',
				value: deposit.bicDeposit,
				tooltip: undefined,
			},
			{
				text: 'IBAN',
				value: deposit.ibanDeposit,
				tooltip: undefined,
			},
			{
				text: 'OCRDeposits',
				value: deposit.ocr,
				tooltip:
					appConfig.country === 'fi'
						? {
								value: t('FIOCRToolTip'),
						  }
						: undefined,
			},
		]
	}

	return array
}
